import { useState, type FormEvent, type JSX } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, type ApiError } from '../../lib/api';
import { usePageMeta } from '../../lib/page-meta';
import { useAuthStore } from '../../state/auth.store';
import { AuthShell, ErrorText, Field, SubmitButton } from '../../components/ui';

/**
 * Sign in with a USERNAME ("Логин") + password (ТЗ §3). On success we return to the page the login wall
 * bounced the visitor from (location.state.from, ТЗ §1) — falling back to the lobby (ТЗ §3) for a fresh
 * sign-in. The device fingerprint is attached by the api layer so the session can silently extend here.
 */
export function LoginPage(): JSX.Element {
  usePageMeta({ title: 'Sign in — Duck Duck Space', noindex: true });
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');

  // The room link the login wall bounced them from (if any). We return there after signing in, AND thread
  // it onto the "Create an account" link so a NEW visitor who signs up instead still lands in that room.
  const fromState = (location.state as { from?: { pathname?: string } } | null)?.from ?? null;
  const from = fromState?.pathname ?? '/lobby';

  const mutation = useMutation({
    mutationFn: api.login,
    onSuccess: (result) => {
      setAuth(result);
      navigate(from, { replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({ login: login.trim(), password });
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to step into your office">
      <form onSubmit={onSubmit}>
        <ErrorText>{mutation.error ? (mutation.error as ApiError).message : null}</ErrorText>
        <Field
          label="Username"
          autoComplete="username"
          required
          value={login}
          onChange={(e) => setLogin(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <SubmitButton loading={mutation.isPending}>Enter the office</SubmitButton>
      </form>
      <p className="mt-5 text-center text-sm text-white/60">
        New here?{' '}
        <Link
          to="/register"
          state={fromState ? { from: fromState } : undefined}
          className="font-medium text-amber-300 hover:text-amber-200"
        >
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
